// ============================================================
// FaceScanner.js — FINAL STABLE BIOMETRIC SYSTEM with Time Tracking
// ============================================================

import React, { useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import * as faceapi from "face-api.js";
import { ref, get, set, update } from "firebase/database";
import { database } from "./firebase";
import { loadModels, matchFace } from "./faceUtils";

const DETECT_INTERVAL = 250;
const BUFFER_SIZE = 4;

export default function FaceScanner({ mode, selectedDate, onAttendance, onStatusCheck }) {
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);

  const employeesCache = useRef([]);
  const bufferRef = useRef([]);
  const lastBoxRef = useRef(null);

  const cooldownRef = useRef(false);
  const lastDetectTime = useRef(0);

  const [message, setMessage] = useState("Initializing...");
  const [confidence, setConfidence] = useState(null);

  // 🔊 unlock voice
  useEffect(() => {
    const unlock = () => {
      speechSynthesis.speak(new SpeechSynthesisUtterance(" "));
      window.removeEventListener("click", unlock);
    };
    window.addEventListener("click", unlock);
    return () => window.removeEventListener("click", unlock);
  }, []);

  const speak = (text) => {
    try {
      speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = "en-IN";
      speechSynthesis.speak(u);
    } catch {}
  };

  // 🔥 INIT
  useEffect(() => {
    const init = async () => {
      try {
        await loadModels();

        const snap = await get(ref(database, "employees"));
        if (snap.exists()) {
          const list = [];
          snap.forEach((c) => {
            const data = c.val();
            list.push({ id: c.key, ...data });
            console.log(`✅ Loaded: ${data.name} (ID: ${c.key})`);
          });
          employeesCache.current = list;
          console.log(`📊 Total employees loaded: ${list.length}`);
        } else {
          console.warn("⚠️ No employees found in database");
        }

        setMessage("Align your face");
      } catch (err) {
        console.error("❌ Init error:", err);
        setMessage("Error loading system");
      }
    };

    init();
  }, []);

  // 🛡️ SAFE DETECTION
  const safeDetect = async (video) => {
    try {
      if (
        !video ||
        video.readyState !== 4 ||
        video.videoWidth === 0 ||
        video.videoHeight === 0
      ) return null;

      const result = await faceapi
        .detectSingleFace(
          video,
          new faceapi.TinyFaceDetectorOptions({
            inputSize: 224,
            scoreThreshold: 0.6,
          })
        )
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (
        !result ||
        !result.detection ||
        !result.detection.box ||
        result.detection.box.x == null
      ) return null;

      return result;
    } catch {
      return null;
    }
  };

  // 🎯 ALIGN CHECK
  const isAligned = (box, video) => {
    const cx = video.videoWidth / 2;
    const cy = video.videoHeight / 2;

    const fx = box.x + box.width / 2;
    const fy = box.y + box.height / 2;

    return Math.abs(cx - fx) < 80 && Math.abs(cy - fy) < 80;
  };

  // 🎨 DRAW BOX (FIXED SCALING)
  const drawUI = (box, aligned) => {
    if (!box) return;

    const canvas = canvasRef.current;
    const video = webcamRef.current?.video;
    if (!canvas || !video) return;

    const ctx = canvas.getContext("2d");

    const w = video.clientWidth;
    const h = video.clientHeight;

    canvas.width = w;
    canvas.height = h;

    const scaleX = w / video.videoWidth;
    const scaleY = h / video.videoHeight;

    ctx.clearRect(0, 0, w, h);

    ctx.shadowColor = aligned ? "#00ff88" : "#ff3b3b";
    ctx.shadowBlur = 20;

    ctx.strokeStyle = aligned ? "#00ff88" : "#ff3b3b";
    ctx.lineWidth = 3;

    ctx.strokeRect(
      box.x * scaleX,
      box.y * scaleY,
      box.width * scaleX,
      box.height * scaleY
    );
  };

  // Get current time in HH:MM:SS AM/PM format
  const getCurrentTime = () => {
    const now = new Date();
    let hours = now.getHours();
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const seconds = now.getSeconds().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const formattedHours = hours.toString().padStart(2, '0');
    return `${formattedHours}:${minutes}:${seconds} ${ampm}`;
  };

  // Parse time string to total minutes for accurate calculation - CORRECTED VERSION
  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr || timeStr === '-' || timeStr === null) return 0;
    
    try {
      let hours = 0, minutes = 0;
      let timeStrClean = timeStr.trim();
      
      // Check if time has AM/PM
      const isPM = timeStrClean.toLowerCase().includes('pm');
      const isAM = timeStrClean.toLowerCase().includes('am');
      
      // Remove AM/PM and get time part
      let timePart = timeStrClean;
      if (isPM || isAM) {
        timePart = timeStrClean.replace(/am|pm/i, '').trim();
      }
      
      // Split hours and minutes (ignore seconds if present)
      const parts = timePart.split(':');
      if (parts.length >= 2) {
        hours = parseInt(parts[0], 10);
        minutes = parseInt(parts[1], 10);
      }
      
      // Validate
      if (isNaN(hours)) hours = 0;
      if (isNaN(minutes)) minutes = 0;
      
      // Convert to 24-hour format
      if (isPM && hours < 12) {
        hours += 12;
      }
      if (isAM && hours === 12) {
        hours = 0;
      }
      
      // Hours should be between 0-23, minutes between 0-59
      hours = Math.min(23, Math.max(0, hours));
      minutes = Math.min(59, Math.max(0, minutes));
      
      return (hours * 60) + minutes;
    } catch (error) {
      console.error('Time parse error:', timeStr, error);
      return 0;
    }
  };

  // Format minutes to hours and minutes string
  const formatHoursAndMinutes = (totalMinutes) => {
    if (totalMinutes <= 0) return "0h 0m";
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };

  // Calculate total hours and minutes between check-in and check-out - CORRECTED
  const calculateTotalTime = (checkInTime, checkOutTime, isHalfDay = false) => {
    if (isHalfDay) return { minutes: 240, display: "4h 0m", decimal: 4.0 };
    if (!checkInTime || !checkOutTime) return { minutes: 0, display: "0h 0m", decimal: 0 };
    
    const checkInMinutes = parseTimeToMinutes(checkInTime);
    const checkOutMinutes = parseTimeToMinutes(checkOutTime);
    
    if (checkInMinutes === 0 || checkOutMinutes === 0) return { minutes: 0, display: "0h 0m", decimal: 0 };
    
    let totalMinutes = checkOutMinutes - checkInMinutes;
    // Handle overnight shifts (e.g., 9:00 PM to 6:00 AM)
    if (totalMinutes < 0) {
      totalMinutes += 24 * 60;
    }
    // Cap at 24 hours
    if (totalMinutes > 24 * 60) {
      totalMinutes = 24 * 60;
    }
    
    const decimal = totalMinutes / 60;
    
    return {
      minutes: totalMinutes,
      display: formatHoursAndMinutes(totalMinutes),
      decimal: Math.round(decimal * 100) / 100
    };
  };

  // ✅ ATTENDANCE with proper time tracking
  const markAttendance = async (user) => {
    if (!user || !user.id) {
      setMessage("❌ Employee data missing");
      return;
    }

    cooldownRef.current = true;
    const currentTime = getCurrentTime();
    const dateToUse = selectedDate || new Date().toISOString().split("T")[0];
    const path = `attendance/${dateToUse}/${user.id}`;

    try {
      const snap = await get(ref(database, path));
      const existing = snap.exists() ? snap.val() : null;

      // CHECK IN
      if (mode === "checkin") {
        if (existing && existing.checkInTime) {
          setMessage(`⚠️ Already checked in at ${existing.checkInTime}`);
          speak(`Already checked in`);
        } else {
          const attendanceData = {
            employeeId: user.id,
            employeeName: user.name,
            employeeRole: user.role || "Staff",
            date: dateToUse,
            checkInTime: currentTime,
            checkOutTime: null,
            halfDay: false,
            totalHours: 0,
            totalMinutes: 0,
            totalTimeDisplay: "0h 0m",
            status: "checked-in",
            markedBy: "face-auth",
            timestamp: Date.now()
          };

          await set(ref(database, path), attendanceData);
          setMessage(`✅ Checked in at ${currentTime}`);
          speak(`Welcome ${user.name}`);
          
          if (onAttendance) {
            onAttendance({ ...attendanceData, mode: "checkin" });
          }
        }
      }

      // CHECK OUT
      else if (mode === "checkout") {
        if (!existing || !existing.checkInTime) {
          setMessage(`⚠️ No check-in found for today`);
          speak(`No check-in found`);
        } else if (existing.checkOutTime) {
          setMessage(`⚠️ Already checked out at ${existing.checkOutTime}`);
          speak(`Already checked out`);
        } else {
          const totalTime = calculateTotalTime(existing.checkInTime, currentTime, false);
          
          const updatedData = {
            ...existing,
            checkOutTime: currentTime,
            totalHours: totalTime.decimal,
            totalMinutes: totalTime.minutes,
            totalTimeDisplay: totalTime.display,
            status: "completed",
            updatedAt: Date.now()
          };

          await update(ref(database, path), updatedData);
          setMessage(`✅ Checked out at ${currentTime} | Worked: ${totalTime.display}`);
          speak(`Goodbye ${user.name}`);
          
          if (onAttendance) {
            onAttendance({ ...updatedData, mode: "checkout" });
          }
        }
      }

      // HALF DAY - Calculate actual time between check-in and half-day mark
      else if (mode === "halfday") {
        if (existing && existing.halfDay) {
          setMessage(`⚠️ Already marked half-day`);
          speak(`Already marked half day`);
        } else if (existing && existing.checkOutTime) {
          setMessage(`⚠️ Already completed full day`);
          speak(`Already completed full day`);
        } else {
          // Use existing check-in time or current time as check-in
          const checkInTimeToUse = existing?.checkInTime || currentTime;
          // For half day, we calculate time from check-in to half-day mark (current time)
          const totalTime = calculateTotalTime(checkInTimeToUse, currentTime, true);
          
          const halfDayData = {
            employeeId: user.id,
            employeeName: user.name,
            employeeRole: user.role || "Staff",
            date: dateToUse,
            checkInTime: checkInTimeToUse,
            checkOutTime: currentTime,
            halfDay: true,
            totalHours: totalTime.decimal,
            totalMinutes: totalTime.minutes,
            totalTimeDisplay: totalTime.display,
            status: "half-day",
            markedBy: "face-auth",
            timestamp: Date.now()
          };

          await set(ref(database, path), halfDayData);
          setMessage(`✅ Half day marked | Worked: ${totalTime.display}`);
          speak(`Half day marked for ${user.name}`);
          
          if (onAttendance) {
            onAttendance({ ...halfDayData, mode: "halfday" });
          }
        }
      }
    } catch (err) {
      console.error("❌ Attendance error:", err);
      setMessage("Error marking attendance");
    }

    bufferRef.current = [];

    setTimeout(() => {
      cooldownRef.current = false;
      setMessage("Ready");
      setConfidence(null);
    }, 5000);
  };

  // 🚀 LOOP
  const loop = async () => {
    const video = webcamRef.current?.video;

    if (
      !video ||
      video.readyState !== 4 ||
      video.videoWidth === 0
    ) {
      requestAnimationFrame(loop);
      return;
    }

    let detection = null;
    const now = Date.now();

    if (now - lastDetectTime.current > DETECT_INTERVAL) {
      lastDetectTime.current = now;
      detection = await safeDetect(video);
    }

    if (detection) {
      lastBoxRef.current = detection.detection.box;
    }

    const box = lastBoxRef.current;

    if (box) {
      const aligned = isAligned(box, video);
      drawUI(box, aligned);

      if (!aligned) {
        setMessage("Align face to center");
        requestAnimationFrame(loop);
        return;
      } else {
        setMessage("Face aligned");
      }
    }

    // 🧠 RECOGNITION
    if (detection && !cooldownRef.current) {
      bufferRef.current.push(Array.from(detection.descriptor));

      if (bufferRef.current.length > BUFFER_SIZE) {
        bufferRef.current.shift();
      }

      if (bufferRef.current.length >= BUFFER_SIZE) {
        const avg = bufferRef.current[0].map((_, i) =>
          bufferRef.current.reduce((s, d) => s + d[i], 0) /
          bufferRef.current.length
        );

        console.log("🔍 Attempting recognition...");
        const result = matchFace(avg, employeesCache.current, 0.62);

        if (result && result.user) {
          setConfidence(result.confidence);
          await markAttendance(result.user);
        } else {
          setMessage("Not recognized");
          console.log("❌ No match found");
        }
      } else {
        setMessage("Hold still...");
      }
    }

    requestAnimationFrame(loop);
  };

  useEffect(() => {
    const animationId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animationId);
  }, [mode, selectedDate]);

  // Get current time display in 12-hour format
  const getCurrentTimeDisplay = () => {
    const now = new Date();
    let hours = now.getHours();
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const seconds = now.getSeconds().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const formattedHours = hours.toString().padStart(2, '0');
    return `${formattedHours}:${minutes}:${seconds} ${ampm}`;
  };

  // Get 24-hour format for display
  const get24HourTime = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const seconds = now.getSeconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  const currentTimeDisplay = getCurrentTimeDisplay();
  const current24HourDisplay = get24HourTime();

  return (
    <div style={{ position: "relative", width: "100%", maxWidth: 480, margin: "auto" }}>
      {/* Time Display */}
      <div style={{
        textAlign: "center",
        marginBottom: "10px",
        padding: "8px",
        background: "#1e293b",
        borderRadius: "8px",
        fontSize: "18px",
        fontWeight: "bold",
        color: "#22c55e"
      }}>
        ⏰ Current Time: {currentTimeDisplay} | {current24HourDisplay}
      </div>

      {/* Webcam */}
      <div style={{ position: "relative" }}>
        <Webcam
          ref={webcamRef}
          audio={false}
          videoConstraints={{ facingMode: "user", width: 640, height: 480 }}
          style={{ width: "100%", borderRadius: 12 }}
        />
        <canvas
          ref={canvasRef}
          style={{ 
            position: "absolute", 
            top: 0, 
            left: 0, 
            width: "100%", 
            height: "100%",
            pointerEvents: "none"
          }}
        />
      </div>

      {/* Status Messages */}
      <div style={{ textAlign: "center", marginTop: "15px" }}>
        <h3 style={{ 
          color: message.includes("✅") ? "#22c55e" : message.includes("⚠️") ? "#f59e0b" : "#fff",
          margin: "5px 0"
        }}>
          {message}
        </h3>
        {confidence && (
          <p style={{ fontSize: "12px", color: "#94a3b8" }}>
            Confidence: {Math.round(confidence * 100)}%
          </p>
        )}
      </div>

      {/* Mode Indicator */}
      <div style={{
        marginTop: "10px",
        padding: "8px",
        background: "#1e293b",
        borderRadius: "8px",
        textAlign: "center"
      }}>
        <span style={{ fontSize: "14px", color: "#94a3b8" }}>
          Current Mode: 
          <strong style={{ 
            color: mode === "checkin" ? "#22c55e" : mode === "checkout" ? "#ef4444" : "#f59e0b",
            marginLeft: "5px"
          }}>
            {mode === "checkin" ? "🟢 CHECK IN" : mode === "checkout" ? "🔴 CHECK OUT" : "🌙 HALF DAY"}
          </strong>
        </span>
      </div>
    </div>
  );
}